import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import clsx from 'clsx';
import { Icon } from './Icon';
// Window chrome owns the crash panel's look; it only ever renders inside one.
import css from '@/wm/Window.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown in the failure message, e.g. `Internet Explorer`. */
  appName: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Keeps one misbehaving app from taking the desktop down with it. A crash
 * shows XP's own apology inside the window that caused it, so the taskbar
 * button, the caption and everything else stay where the user left them.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error(`[${this.props.appName}] crashed`, error, info.componentStack);
    }
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const detail = error.stack ?? `${error.name}: ${error.message}`;

    return (
      <div className={css.crash} role="alert">
        <div className={css.crashHead}>
          <Icon src="/assets/icons/msg-error.svg" size={32} />
          <div>
            <p className={css.crashTitle}>
              {this.props.appName} has encountered a problem and needs to close.
            </p>
            <p className={css.crashText}>We are sorry for the inconvenience.</p>
          </div>
        </div>

        <div className={clsx('sunken-panel', css.crashDetail)}>{detail}</div>

        <div className={css.crashActions}>
          <button type="button" onClick={this.handleReset}>
            Close
          </button>
        </div>
      </div>
    );
  }
}
